import AeroplaneSvg from '@/assets/flight/icons/aeroplane.svg?react'
import FoodSvg from '@/assets/food/icons/pizza.svg?react'
import BusinessSvg from '@/assets/icons/business.svg?react'
import logoSmSvg from '@/assets/icons/logo-sm.svg'
import ShoppingSvg from '@/assets/icons/shopping.svg?react'
import StoresSvg from '@/assets/icons/stores.svg?react'
import LogoSvg from '@/assets/images/logo.svg?react'
import useCurrentVertical, {Vertical} from '@/hooks/useCurrentVertical'
import useSiteHeader from '@/lib/common/hooks/useSiteHeader'
import {useIsFoodGrubhubAvailableInCurrentCountry} from '@/lib/food-grubhub/hooks/useIsFoodAvailableInCurrentCountry'
import isInApp from '@/lib/in-app/utils/isInApp'
import {useLocale} from '@/lib/locale/locale'
import {
  BusinessRoute,
  FlightRoute,
  FoodGrubhubRoute,
  HomeRoute,
  IndexRoute,
  StoresRoute,
} from '@/routeRegistry'
import {FLIGHT_ROUTE_DEFAULT_SEARCH} from '@/routes/_flight/flights'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import BetterSuspense from '@/utils/components/BetterSuspense'
import useWindowSize from '@/utils/hooks/useWindowSize'

import CartLink from './CartLink'
import style from './Navbar.module.scss'
import {type SearchInputControl} from './SearchInputWithAutocomplete'

const Sidebar = lazy(async () => await import('./Sidebar'))
const NavbarEnd = lazy(async () => await import('./NavbarEnd'))
const SearchInputWithAutocomplete = lazy(async () => await import('./SearchInputWithAutocomplete'))
const CountrySelector = lazy(async () => await import('@/lib/locale/components/CountrySelector'))

interface Props {
  variant?: 'light' | 'dark'
  withSearch?: boolean
  withoutLogoOnDesktop?: boolean
  noLogo?: boolean
  transparentBg?: boolean
  searchInputRef?: Ref<SearchInputControl>
  className?: string
  displayNavBanner?: boolean
  haveCart?: boolean
  navBannerContent?: string
  onSearchReady?: () => void
}

export default memo<Props>(function Navbar({
  variant,
  withSearch,
  searchInputRef,
  withoutLogoOnDesktop,
  noLogo,
  transparentBg,
  className,
  displayNavBanner,
  haveCart = true,
  navBannerContent: navBannerContentOverride,
  onSearchReady,
}) {
  variant = variant ?? undefined

  const {isMobile} = useWindowSize()
  const [locale] = useLocale()
  const {pathname, search} = useLocation()
  const currentVertical = useCurrentVertical()

  const isFoodGrubhubAvailableInCurrentCountry = useIsFoodGrubhubAvailableInCurrentCountry()

  const shouldShowSearch = (() => {
    const isHomeWithoutSearch = pathname === HomeRoute.fullPath && !search.q
    return Boolean(withSearch) && !isHomeWithoutSearch
  })()

  const logo = (() => {
    if (noLogo) return null
    if (isMobile) {
      return 'sm'
    }
    if (withoutLogoOnDesktop) {
      return null
    }
    return 'lg'
  })()

  const {data: siteBannerContent} = useSiteHeader(locale)
  const navBannerContent = navBannerContentOverride ?? siteBannerContent

  if (isInApp()) {
    return null
  }

  return (
    <header
      className={clsx(
        style.Header,
        variant === 'dark' && style.Dark,
        transparentBg && style.Transparent,
        className,
      )}
      id='header'
    >
      <div className={clsx(style.Navbar, shouldShowSearch && style.WithSearch)}>
        {isMobile && (
          <>
            <Suspense>
              <Sidebar variant={variant} />
            </Suspense>
            <Link
              to={IndexRoute.fullPath}
              className={clsx(
                style.LogoLink,
                transparentBg && !shouldShowSearch && style.LogoExtended,
              )}
            >
              {logo === 'sm' && <img src={logoSmSvg} alt='' />}
              <VisuallyHidden>InnoSearch</VisuallyHidden>
            </Link>
          </>
        )}
        {!isMobile && (
          <Link
            to={IndexRoute.fullPath}
            className={clsx(
              style.LogoLink,
              transparentBg && !shouldShowSearch && style.LogoExtended,
            )}
          >
            {logo === 'sm' && <img src={logoSmSvg} alt='' />}
            {logo === 'lg' && <LogoSvg className={style.Logo} aria-hidden='true' />}
            <VisuallyHidden>InnoSearch</VisuallyHidden>
          </Link>
        )}
        {!transparentBg && (
          <ul className={clsx(style.Products, !shouldShowSearch && style.Extended)}>
            <li>
              <Link
                to={HomeRoute.fullPath}
                className={clsx(style.Product, currentVertical === Vertical.Shop && style.Active)}
                // className={clsx(style.Product)}
                activeProps={{className: style.Active}}
                aria-label='Shopping'
              >
                <ShoppingSvg />
                <span aria-hidden='true' className={style.NavLinkText}>
                  Shop
                </span>
              </Link>
            </li>

            <li>
              <Link
                to={FlightRoute.fullPath}
                search={FLIGHT_ROUTE_DEFAULT_SEARCH}
                className={clsx(style.Product, currentVertical === Vertical.Flight && style.Active)}
                activeProps={{className: style.Active}}
                aria-label='Flights'
              >
                <AeroplaneSvg />
                <span aria-hidden='true' className={style.NavLinkText}>
                  Fly
                </span>
              </Link>
            </li>
            {isFoodGrubhubAvailableInCurrentCountry && (
              <li>
                <Link
                  to={FoodGrubhubRoute.fullPath}
                  className={clsx(
                    style.Product,
                    currentVertical === Vertical.FoodGrubHub && style.Active,
                  )}
                  activeProps={{className: style.Active}}
                  aria-label='Eat'
                >
                  <FoodSvg />
                  <span aria-hidden='true' className={style.NavLinkText}>
                    Eat
                  </span>
                </Link>
              </li>
            )}
            <li>
              <Link
                to={StoresRoute.fullPath}
                className={clsx(style.Product)}
                activeProps={{className: style.Active}}
                aria-label='Stores'
              >
                <StoresSvg />
                <span aria-hidden='true' className={style.NavLinkText}>
                  Stores
                </span>
              </Link>
            </li>
            <li>
              <Link
                to={BusinessRoute.fullPath}
                className={clsx(style.Product)}
                activeProps={{className: style.Active}}
                aria-label='Business'
              >
                <BusinessSvg />
                <span aria-hidden='true' className={style.NavLinkText}>
                  Business
                </span>
              </Link>
            </li>
          </ul>
        )}

        {shouldShowSearch && (
          <div className={clsx(style.Search, haveCart && style.WithCart)}>
            <BetterSuspense
              fallback={<div style={{width: '100%'}} className={style.SearchFallback} />}
              onReady={onSearchReady}
            >
              <SearchInputWithAutocomplete ref={searchInputRef} />
            </BetterSuspense>
          </div>
        )}

        {!isMobile ? (
          <Suspense fallback={<div style={{height: '2.625rem'}} />}>
            <NavbarEnd variant={variant} haveCart={haveCart} transparentBg={transparentBg} />
          </Suspense>
        ) : (
          <div className={style.EndSection}>
            <div className={style.CartContainer}>
              {haveCart && (
                <CartLink
                  className={clsx(style.CartLink, shouldShowSearch && style.CartWithSearch)}
                />
              )}
            </div>
            <Suspense>{<CountrySelector variant={variant} />}</Suspense>
          </div>
        )}
      </div>
      {displayNavBanner && !!navBannerContent && (
        <div
          role='region'
          aria-label='Promotional banner'
          className={style.NavBanner}
          // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- Ignore
          dangerouslySetInnerHTML={{
            __html: navBannerContent,
          }}
        />
      )}
    </header>
  )
})
